/**
 * src/pages/Calendar.jsx — weekly calendar with appointment management.
 *
 * A week at a time, one column per day, filterable by artist. A studio with
 * four artists and overlapping chairs is unreadable as a single merged column,
 * so the artist filter is the primary control rather than an afterthought.
 *
 * Clicking an appointment opens a drawer with its detail, its consent status
 * and the actions an artist actually needs mid-day: reschedule, add session
 * notes, mark completed, cancel.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useApi } from '../lib/useApi.js';
import {
  formatDateTime,
  formatTime,
  localInputToUnix,
  startOfWeek,
  unixToLocalInput,
} from '../lib/format.js';
import { zonedParts, zonedTimeToUnix } from '../lib/timezone.js';
import { ErrorBanner, Loader, PageHeader, Pill } from '../components/ui.jsx';
import { Dialog } from '../components/ui/Dialog.jsx';
import DownloadButton from '../components/DownloadButton.jsx';
import { openNativePicker } from '../lib/picker.js';

/**
 * Statuses that mean the appointment is finished, one way or another. Mirrors
 * CLOSED_STATUSES in api/src/routes/appointments.js.
 */
const CLOSED_STATUSES = ['completed', 'no_show', 'cancelled'];

/** Colour key shown under the grid, so the colours are not a guessing game. */
const LEGEND = [
  { status: 'confirmed', label: 'Confirmed' },
  { status: 'pending', label: 'Awaiting deposit' },
  { status: 'completed', label: 'Completed' },
  { status: 'no_show', label: 'No-show' },
  { status: 'cancelled', label: 'Cancelled' },
];

export default function Calendar() {
  const { timezone } = useAuth();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), timezone));
  const [artistId, setArtistId] = useState('');
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  // Hides everything that is done with — completed, no-show, cancelled —
  // so a busy week collapses to just the work still ahead. Off by default:
  // a calendar that silently omits appointments is worse than a full one.
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  // Mobile burger for the header controls — see the toggle's comment below.
  const [controlsOpen, setControlsOpen] = useState(false);
  const activeFilters = (artistId ? 1 : 0) + (upcomingOnly ? 1 : 0);

  const weekEnd = weekStart + 7 * 86400;
  const query = `/api/appointments?from=${weekStart}&to=${weekEnd}${artistId ? `&artistId=${artistId}` : ''}`;

  const { data, error, loading, reload } = useApi(query, [weekStart, artistId]);
  const artists = useApi('/api/artists');

  const days = Array.from({ length: 7 }, (_, i) => weekStart + i * 86400);
  // Today's midnight in the studio's zone — not the browser's — so "today" is
  // highlighted consistently for every artist regardless of where they are.
  const todayInStudio = zonedParts(Math.floor(Date.now() / 1000), timezone);
  const todayMidnight = zonedTimeToUnix(
    `${todayInStudio.year}-${String(todayInStudio.month).padStart(2, '0')}-${String(todayInStudio.day).padStart(2, '0')}`,
    '00:00',
    timezone,
  );
  const todayStart = startOfWeek(new Date(), timezone) === weekStart ? todayMidnight : null;

  return (
    <>
      <PageHeader
        title="Calendar"
        className="calendar-header"
        subtitle={`${formatDateTime(weekStart, timezone).split(',')[0]} – ${formatDateTime(weekEnd - 86400, timezone).split(',')[0]}`}
        actions={
          <>
            {/* Mobile only (styles.css): the controls below don't fit on a
                phone in one row, so they fold behind this burger. Above the
                breakpoint it is hidden and the controls sit inline as
                before. The dot says a filter is hiding appointments even
                while the menu is closed. */}
            <button
              type="button"
              className="menu-toggle calendar-controls-toggle"
              aria-expanded={controlsOpen}
              aria-controls="calendar-controls"
              aria-label={`${controlsOpen ? 'Hide' : 'Show'} calendar filters${activeFilters ? ` (${activeFilters} active)` : ''}`}
              onClick={() => setControlsOpen((open) => !open)}
            >
              <span className="menu-toggle-bars" aria-hidden="true" />
              {activeFilters ? <span className="calendar-controls-dot" aria-hidden="true" /> : null}
            </button>
            <div id="calendar-controls" className={`calendar-controls${controlsOpen ? ' is-open' : ''}`}>
              <select
                value={artistId}
                onChange={(e) => setArtistId(e.target.value)}
                aria-label="Filter by artist"
                className="calendar-artist-filter"
              >
                <option value="">All artists</option>
                {(artists.data?.artists ?? []).map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <div className="row calendar-week-nav">
                <button
                  type="button"
                  className="btn"
                  aria-label="Previous week"
                  onClick={() => setWeekStart((w) => w - 7 * 86400)}
                >
                  ←
                </button>
                <button type="button" className="btn" onClick={() => setWeekStart(startOfWeek(new Date(), timezone))}>
                  This week
                </button>
                <button
                  type="button"
                  className="btn"
                  aria-label="Next week"
                  onClick={() => setWeekStart((w) => w + 7 * 86400)}
                >
                  →
                </button>
              </div>
              <label className="btn row" style={{ fontWeight: 600, gap: 6 }} title="Hide completed, no-shows and cancellations">
                <input
                  type="checkbox"
                  checked={upcomingOnly}
                  onChange={(e) => setUpcomingOnly(e.target.checked)}
                  style={{ width: 'auto', margin: 0 }}
                />
                Upcoming only
              </label>
            </div>
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              New appointment
            </button>
          </>
        }
      />

      <Loader loading={loading} error={error}>
        <div className="calendar-grid">
          {days.map((dayStart) => {
            const dayEnd = dayStart + 86400;
            const events = (data?.appointments ?? []).filter(
              (a) =>
                a.startsAt >= dayStart &&
                a.startsAt < dayEnd &&
                (!upcomingOnly || !CLOSED_STATUSES.includes(a.status)),
            );
            return (
              <div
                key={dayStart}
                className={`calendar-day${todayStart === dayStart ? ' today' : ''}`}
              >
                <div className="calendar-day-label">
                  {new Date(dayStart * 1000).toLocaleDateString(undefined, {
                    weekday: 'short',
                    day: 'numeric',
                    timeZone: timezone,
                  })}
                </div>
                {events.length === 0 ? (
                  <span className="muted small">—</span>
                ) : (
                  events.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={`calendar-event event-${a.status}`}
                      onClick={() => setOpenId(a.id)}
                    >
                      <span className="mono">{formatTime(a.startsAt, timezone)}</span> {a.clientName}
                      <div className="muted" style={{ fontSize: 11 }}>{a.artistName}</div>
                    </button>
                  ))
                )}
              </div>
            );
          })}
        </div>
        <div className="legend row wrap" style={{ marginTop: 16 }}>
          {LEGEND.map((l) => (
            <span key={l.status} className="row small muted" style={{ gap: 6 }}>
              <span className={`legend-swatch event-${l.status}`} aria-hidden="true" />
              {l.label}
            </span>
          ))}
        </div>
      </Loader>

      {openId ? (
        <AppointmentDrawer
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => {
            reload();
            setOpenId(null);
          }}
        />
      ) : null}

      {creating ? (
        <NewAppointment
          artists={artists.data?.artists ?? []}
          onClose={() => setCreating(false)}
          onCreated={() => {
            reload();
            setCreating(false);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The deposit on a paid appointment: whether it's been refunded or disputed,
 * and an owner-only "Refund deposit" action.
 *
 * Refunding goes through POST /api/appointments/:id/refund, which refunds the
 * whole deposit through Stripe and takes it back from the studio's Stripe
 * balance. It doesn't cancel the appointment; that's the separate button
 * below. Not offered while a dispute is open or after one is lost, when the
 * bank already has the money.
 *
 * @param {{ appt: Record<string, any>, timezone: string, onChanged: () => void }} props
 * @returns {JSX.Element}
 */
function DepositCard({ appt, timezone, onChanged }) {
  const { artist } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const dispute = appt.depositDisputeStatus;
  const canRefund =
    artist?.role === 'owner' && !appt.depositRefundedAt && dispute !== 'open' && dispute !== 'lost';

  const status = appt.depositRefundedAt
    ? `Refunded ${formatDateTime(appt.depositRefundedAt, timezone)}`
    : dispute === 'open'
      ? "Disputed by the client's bank. Check your email for what to send."
      : dispute === 'lost'
        ? 'Dispute lost: the bank returned the deposit to the client.'
        : dispute === 'won'
          ? 'Dispute won: the deposit was paid back to you.'
          : 'Paid';

  const refund = async () => {
    if (!window.confirm('Refund this deposit in full to the client? It comes out of your Stripe balance.')) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/appointments/${appt.id}/refund`, {});
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="row-between">
        <div>
          <h3>Deposit</h3>
          <p className="muted small">{status}</p>
        </div>
        {canRefund ? (
          <button type="button" className="btn btn-sm" disabled={busy} onClick={refund}>
            {busy ? 'Refunding…' : 'Refund deposit'}
          </button>
        ) : null}
      </div>
      <ErrorBanner>{error}</ErrorBanner>
    </div>
  );
}

/**
 * Detail drawer for one appointment.
 *
 * @param {{ id: string, onClose: () => void, onChanged: () => void }} props
 * @returns {JSX.Element}
 */
function AppointmentDrawer({ id, onClose, onChanged }) {
  const { timezone } = useAuth();
  const { data, error, loading, reload } = useApi(`/api/appointments/${id}`);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [notes, setNotes] = useState(null);
  const [when, setWhen] = useState(null);

  const appt = data?.appointment;
  const consent = data?.consent;

  /**
   * Applies a patch and refreshes both the drawer and the week behind it.
   *
   * @param {Record<string, unknown>} patch
   */
  const save = async (patch) => {
    setBusy(true);
    setActionError(null);
    try {
      await api.patch(`/api/appointments/${id}`, patch);
      onChanged();
    } catch (err) {
      setActionError(err.message);
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await api.del(`/api/appointments/${id}`);
      onChanged();
    } catch (err) {
      setActionError(err.message);
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await api.post(`/api/appointments/${id}/consent/resend`);
      reload();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title="Appointment" onClose={onClose}>
      <Loader loading={loading} error={error}>
        {appt ? (
          <div className="stack">
            <ErrorBanner>{actionError}</ErrorBanner>

            <div className="row-between">
              <div>
                <h3>
                  <Link to={`/clients/${appt.clientId}`}>{appt.clientName}</Link>
                </h3>
                <p className="muted small">
                  {appt.artistName}
                  {appt.projectTitle ? ` · ${appt.projectTitle}` : ''}
                </p>
              </div>
              <Pill status={appt.status} />
            </div>

            <div>
              <label htmlFor="when">Starts</label>
              <div className="row">
                <input
                  id="when"
                  type="datetime-local"
                  value={when ?? unixToLocalInput(appt.startsAt, timezone)}
                  onChange={(e) => setWhen(e.target.value)}
                  onClick={openNativePicker}
                />
                <button
                  type="button"
                  className="btn"
                  disabled={busy || !when}
                  onClick={() => {
                    const startsAt = localInputToUnix(when, timezone);
                    // Keep the session the same length when it moves.
                    save({ startsAt, endsAt: startsAt + (appt.endsAt - appt.startsAt) });
                  }}
                >
                  Move
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="notes">Session notes</label>
              <textarea
                id="notes"
                value={notes ?? appt.notes ?? ''}
                onChange={(e) => setNotes(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-sm"
                style={{ marginTop: 8 }}
                disabled={busy || notes === null}
                onClick={() => save({ notes })}
              >
                Save notes
              </button>
            </div>

            <div className="card" style={{ padding: 14 }}>
              <div className="row-between">
                <div>
                  <h3>Consent form</h3>
                  <p className="muted small">
                    {consent
                      ? consent.status === 'signed'
                        ? `Signed by ${consent.signerName} · ${formatDateTime(consent.signedAt, timezone)}`
                        : `Link expires ${formatDateTime(consent.expiresAt, timezone)}`
                      : 'Not created yet — confirm the appointment to send one.'}
                  </p>
                </div>
                <Pill status={consent?.status} />
              </div>
              {consent ? (
                <div className="row" style={{ marginTop: 10 }}>
                  {consent.pdfPath ? (
                    <DownloadButton path={consent.pdfPath} filename={`consent-form-${consent.id}.pdf`}>
                      Download PDF
                    </DownloadButton>
                  ) : null}
                  {consent.status !== 'signed' ? (
                    <button type="button" className="btn btn-sm" disabled={busy} onClick={resend}>
                      Resend link
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {appt.depositPaid ? <DepositCard appt={appt} timezone={timezone} onChanged={reload} /> : null}

            <div className="row wrap">
              {appt.status !== 'confirmed' && appt.status !== 'cancelled' ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => save({ status: 'confirmed' })}
                >
                  Confirm &amp; send consent
                </button>
              ) : null}
              {appt.status !== 'completed' && appt.status !== 'cancelled' ? (
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => save({ status: 'completed' })}
                >
                  Mark completed
                </button>
              ) : null}
              {appt.status !== 'no_show' && appt.status !== 'cancelled' ? (
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => save({ status: 'no_show' })}
                  title="Client did not turn up. No aftercare email is sent, and the slot frees up."
                >
                  Mark no-show
                </button>
              ) : null}
              {appt.status !== 'cancelled' ? (
                <button type="button" className="btn btn-danger" disabled={busy} onClick={cancel}>
                  Cancel appointment
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </Loader>
    </Dialog>
  );
}

/**
 * Form for booking a client in by hand.
 *
 * @param {{ artists: object[], onClose: () => void, onCreated: () => void }} props
 * @returns {JSX.Element}
 */
function NewAppointment({ artists, onClose, onCreated }) {
  const { timezone } = useAuth();
  const clients = useApi('/api/clients');
  const [form, setForm] = useState({
    clientId: '',
    artistId: '',
    startsAt: '',
    hours: 3,
    projectId: '',
    isTouchUp: false,
    notes: '',
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Projects are filtered to the chosen client so the dropdown stays short.
  const projects = useApi(form.clientId ? `/api/projects?clientId=${form.clientId}` : null, [
    form.clientId,
  ]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const startsAt = localInputToUnix(form.startsAt, timezone);
      await api.post('/api/appointments', {
        clientId: form.clientId,
        artistId: form.artistId,
        startsAt,
        endsAt: startsAt + Math.round(Number(form.hours) * 3600),
        projectId: form.projectId || undefined,
        isTouchUp: form.isTouchUp,
        notes: form.notes || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  return (
    <Dialog title="New appointment" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>

        <div>
          <label htmlFor="client">Client</label>
          <select id="client" value={form.clientId} onChange={set('clientId')} required>
            <option value="">Choose a client…</option>
            {(clients.data?.clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="artist">Artist</label>
          <select id="artist" value={form.artistId} onChange={set('artistId')} required>
            <option value="">Choose an artist…</option>
            {artists.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        {(projects.data?.projects ?? []).length > 0 ? (
          <div>
            <label htmlFor="project">Part of a project</label>
            <select id="project" value={form.projectId} onChange={set('projectId')}>
              <option value="">One-off session</option>
              {(projects.data?.projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="row">
          <div className="grow">
            <label htmlFor="startsAt">Starts</label>
            <input
              id="startsAt"
              type="datetime-local"
              value={form.startsAt}
              onChange={set('startsAt')}
              onClick={openNativePicker}
              required
            />
          </div>
          <div style={{ width: 110 }}>
            <label htmlFor="hours">Hours</label>
            <input
              id="hours"
              type="number"
              min="0.5"
              step="0.5"
              value={form.hours}
              onChange={set('hours')}
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="apptNotes">Notes</label>
          <textarea id="apptNotes" value={form.notes} onChange={set('notes')} />
        </div>

        <label className="row small" style={{ fontWeight: 400 }}>
          <input
            type="checkbox"
            checked={form.isTouchUp}
            onChange={set('isTouchUp')}
            style={{ width: 'auto' }}
          />
          Touch-up session (no deposit)
        </label>

        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Booking…' : 'Book and send consent form'}
        </button>
      </form>
    </Dialog>
  );
}
